import {
  BugOutlined,
  HomeOutlined,
  MenuOutlined,
  ReloadOutlined,
  SettingOutlined,
  SwapOutlined,
  UserOutlined,
  WifiOutlined,
} from "@ant-design/icons"
import { Layout, Menu } from "antd"
import Sider from "antd/es/layout/Sider"
import { Header, Content, Footer } from "antd/es/layout/layout"
import { FC, ReactElement, ReactNode, useEffect, useMemo, useState } from "react"
import { Link, Switch, Route, Redirect } from "react-router-dom"
import { FlexCol, FlexRow } from "./components/commons-ts/common"
import { LoginPage } from "./pages/LoginPage"
import { CENTRAL_CONTAINER_MAX_WIDTH, useMediaQuery } from "./utils/utils"
import { Translated } from "./utils/translated"
import { EGeneralSettingsTabKey, SettingsGeneral } from "./pages/SettingsGeneralPage"
import {
  faHardDrive,
  faSatellite,
  faBuilding,
  faClipboard,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { ReactComponent as HicIcon } from "./svg/hic-icon.svg"
import { ICloudUser } from "./types"
import {
  callCloudApiV2,
  setLocalStorageCloudApiUser,
  getLocalStorageCloudApiUser,
} from "./utils/cloudApiV2"
import { DevicesConnectionWidgetBLE } from "./components/commons-ts/deviceConnectionWidgetBLE"
import { DeviceSensorsWidgetBLE } from "./components/commons-ts/deviceSensorsWidgetBLE"
import { HelpWidget } from "./components/commons-ts/helpWidget"
import { TranslationLanguageSelectWidget } from "./components/commons-ts/translationLanguageSelectWidget"
import { UserWidget } from "./components/commons-ts/userWidget"
import { SitePage } from "./pages/01_Site"
import { EquipmentPage } from "./pages/02_Equipment"
import { ZonePage } from "./pages/03_Zone"
import { ImpactPage } from "./pages/04_Impact"
import { BleConnectPage } from "./pages/BLEConnectPage"
import { HomePage } from "./pages/Home/00_HomePage"
import { HomePage_HIC } from "./pages/Home/01_HomeHIC"
import { ReportMasterPage } from "./pages/ReportPage"
import { EDeviceSettingsTabKey, SettingsDevice } from "./pages/SettingsDevicePage"

const PrivateRoute: FC<{
  path: string
  children: ReactNode
}> = ({ children, path }) => {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null)

  useEffect(() => {
    callCloudApiV2<ICloudUser>("/me")
      .then(({ resp, entity: user }) => {
        // Explicitly unauthorized
        if (resp.status === 401) {
          console.log(`PrivateRoute: explicitly unauthorized, logging out..`)
          setLocalStorageCloudApiUser(null)
          setIsAuthorized(false)
          return
        }
        // Not unauthorized but null user
        if (user === null) {
          console.log(
            `PrivateRoute: not unauthorized but null user, will use the local storage user if any..`,
          )
          user = getLocalStorageCloudApiUser()
          if (user === null) {
            console.log(`PrivateRoute: no local storage user either, logging out..`)
            setLocalStorageCloudApiUser(null)
            setIsAuthorized(false)
            return
          }
        }
        // Authorized and user OK (either from fetch or local storage)
        console.log(`PrivateRoute: connected as`, user.UUID)
        setLocalStorageCloudApiUser(user)
        setIsAuthorized(true)
      })
      .catch((err: any) => {
        // Fetch request error (e.g. network connection error)
        console.log(`PrivateRoute: fetch error`, err)
        let user = getLocalStorageCloudApiUser()
        if (user !== null) {
          console.log(`PrivateRoute: fetch error, using local storage user`)
          setIsAuthorized(true)
          return
        }
        // No remote user nor local storage user => not authorized
        setIsAuthorized(false)
      })
  }, [])

  if (isAuthorized === null) {
    return null
  }
  if (isAuthorized === false) {
    return <Redirect to="/login" />
  }

  return <Route path={path}>{children}</Route>
}

const MainLayout: FC = () => {
  let isMobile = useMediaQuery()
  const [isHeaderMenuExpanded, setIsHeaderMenuExpanded] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(true)

  const menuItems = [
    {
      key: "menu-key-home",
      icon: <HomeOutlined />,
      label: (
        <Link to="/" style={{ textDecoration: "none" }}>
          <Translated keyEn="Home" />
        </Link>
      ),
    },
    {
      key: "menu-key-device-connect",
      icon: <SwapOutlined />,
      label: (
        <Link to={`/connect`} style={{ textDecoration: "none" }}>
          <Translated keyEn="Connect" />
        </Link>
      ),
    },
    {
      key: "menu-key-device",
      icon: <FontAwesomeIcon icon={faHardDrive} />,
      label: (
        <Link
          to={`/settings/device`}
          style={{
            textDecoration: "none",
            color: "#fff",
          }}
        >
          <Translated keyEn="Device" />
        </Link>
      ),
      children: [
        {
          key: "menu-key-device-history",
          icon: <FontAwesomeIcon icon={faClipboard} />,
          label: (
            <Link
              to={`/settings/device/${EDeviceSettingsTabKey.DeviceHistory}`}
              style={{
                textDecoration: "none",
                color: "#fff",
              }}
            >
              <Translated keyEn="History" />
            </Link>
          ),
        },
        {
          key: "menu-key-device-station",
          icon: <FontAwesomeIcon icon={faHardDrive} />,
          label: (
            <Link
              to={`/settings/device/${EDeviceSettingsTabKey.StationConfig}`}
              style={{
                textDecoration: "none",
                color: "#fff",
              }}
            >
              <Translated keyEn="Station" />
            </Link>
          ),
        },
        {
          key: "menu-key-device-hic",
          icon: (
            <HicIcon
              width={24}
              style={{
                marginLeft: -3,
                marginBottom: -7,
                fill: "#fff",
              }}
            />
          ),
          label: (
            <Link
              to={`/settings/device/${EDeviceSettingsTabKey.HeadHICConfig}`}
              style={{
                textDecoration: "none",
                color: "#fff",
              }}
            >
              <Translated keyEn="Head HIC" />
            </Link>
          ),
        },
        {
          key: "menu-key-device-wifi",
          icon: <WifiOutlined />,
          label: (
            <Link
              to={`/settings/device/${EDeviceSettingsTabKey.WifiConfig}`}
              style={{
                textDecoration: "none",
                color: "#fff",
              }}
            >
              <Translated keyEn="WiFi" />
            </Link>
          ),
        },
        {
          key: "menu-key-device-gnss",
          icon: <FontAwesomeIcon icon={faSatellite} />,
          label: (
            <Link
              to={`/settings/device/${EDeviceSettingsTabKey.GNSSConfig}`}
              style={{
                textDecoration: "none",
                color: "#fff",
              }}
            >
              <Translated keyEn="GNSS" />
            </Link>
          ),
        },
      ],
    },
    {
      key: "menu-key-settings",
      icon: <SettingOutlined />,
      label: <Translated keyEn="Settings" />,
      children: [
        {
          key: "menu-key-settings-general-user-account",
          icon: <UserOutlined />,
          label: (
            <Link
              to={`/settings/general/${EGeneralSettingsTabKey.UserAccount}`}
              style={{ textDecoration: "none" }}
            >
              <Translated keyEn="User account" />
            </Link>
          ),
        },
        {
          key: "menu-key-settings-general-enterprise-account",
          icon: <FontAwesomeIcon icon={faBuilding} />,
          label: (
            <Link
              to={`/settings/general/${EGeneralSettingsTabKey.EnterpriseAccount}`}
              style={{ textDecoration: "none" }}
            >
              <Translated keyEn="Enterprise account" />
            </Link>
          ),
        },
        {
          key: "menu-key-settings-general-updates",
          icon: <ReloadOutlined />,
          label: (
            <Link
              to={`/settings/general/${EGeneralSettingsTabKey.Updates}`}
              style={{
                textDecoration: "none",
                color: "#fff",
              }}
            >
              <Translated keyEn="Updates" />
            </Link>
          ),
        },
        {
          key: "menu-key-debug",
          icon: <BugOutlined />,
          label: (
            <Link
              to={`/settings/general/${EGeneralSettingsTabKey.Debug}`}
              style={{
                textDecoration: "none",
                color: "#fff",
              }}
            >
              <Translated keyEn="Debug" />
            </Link>
          ),
        },
      ],
    },
  ]

  const memoHeaderContent = useMemo(() => {
    if (isMobile) {
      let firstRow = (
        <FlexRow
          style={{
            justifyContent: "space-between",
            alignItems: "center",
            height: "100%",
            paddingRight: 20,
          }}
        >
          <Link to="/">
            <img
              style={{ marginLeft: "5px" }}
              alt="logo"
              src="/icon512-lugsoft-rebounce-blue-on-transparent.png"
              width={64 - 10}
            />
          </Link>
          <DevicesConnectionWidgetBLE />
          <FlexRow style={{ alignItems: "center" }}>
            <UserWidget />
            <MenuOutlined
              style={{ fontSize: "1.5rem" }}
              onClick={() => {
                setIsHeaderMenuExpanded(!isHeaderMenuExpanded)
              }}
            />
          </FlexRow>
        </FlexRow>
      )
      let secondRow: ReactElement | null = null
      if (isHeaderMenuExpanded) {
        secondRow = (
          <FlexRow
            style={{
              justifyContent: "end",
              alignItems: "center",
              height: "100%",
              paddingRight: 20,
            }}
          >
            <TranslationLanguageSelectWidget />
            <HelpWidget />
          </FlexRow>
        )
      }
      return (
        <FlexCol
          style={{
            gap: 0,
          }}
        >
          {firstRow}
          {secondRow}
        </FlexCol>
      )
    } else {
      return (
        <FlexRow
          style={{
            justifyContent: "space-between",
            alignItems: "center",
            height: "100%",
            paddingLeft: 20,
            paddingRight: 20,
          }}
        >
          <div
            style={{
              width: 150,
              overflow: "visible",
            }}
          >
            <DeviceSensorsWidgetBLE />
          </div>
          <DevicesConnectionWidgetBLE />
          <FlexRow style={{ alignItems: "center" }}>
            <TranslationLanguageSelectWidget />
            <HelpWidget />
            <UserWidget />
          </FlexRow>
        </FlexRow>
      )
    }
  }, [isMobile, isHeaderMenuExpanded])

  return (
    // Layout0: Sider + Layout1
    <Layout style={{ height: "100dvh" }}>
      <Sider
        collapsible
        onCollapse={(collapsed) => {
          setIsCollapsed(collapsed)
        }}
        collapsed={isCollapsed}
        defaultCollapsed={true}
        hidden={isMobile}
      >
        <FlexCol
          style={{
            height: "100%",
            justifyContent: "space-between",
          }}
        >
          <Link to="/" style={{ textDecoration: "none" }}>
            <FlexRow
              style={{
                alignItems: "center",
                gap: 5,
                height: "100%",
                width: "100%",
                paddingLeft: 10,
                paddingTop: 16,
              }}
            >
              <img src="/icon512-lugsoft-rebounce-white-on-transparent.png" width={48} />
              <span className="lugsoft-logo-title" style={{ opacity: isCollapsed ? 0 : 1 }}>
                <Translated keyEn="Lugsoft Universal" />
              </span>
            </FlexRow>
          </Link>
          {/* <Link to="/" style={{ textDecoration: "none" }}>
            <FlexRow
              style={{
                width: "100%",
                gap: isCollapsed ? 0 : 10,
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s linear",
                margin: 10,
              }}
            >
              <span
                style={{
                  color: "#fff",
                  fontSize: "1.3rem",
                  transition: "all 0.2s linear",
                  visibility: isCollapsed ? "hidden" : "visible",
                }}
              >
                Lugsoft
              </span>
            </FlexRow>
          </Link> */}
          <Menu theme="dark" mode="inline" items={menuItems} />
          <div />
        </FlexCol>
      </Sider>
      {/* Layout1: Header + Content + Footer + Menu */}
      <Layout>
        <Header
          style={{
            padding: 0,
            position: "sticky",
            top: 0,
            zIndex: 500,
            lineHeight: "1em",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
            height: "fit-content",
            paddingBottom: 5,
          }}
        >
          {memoHeaderContent}
        </Header>
        <Content
          style={{
            overflow: "scroll",
          }}
        >
          <div
            style={{
              height: "100%",
              width: "100%",
              padding: isMobile ? "10px" : "20px",
              margin: "auto",
              maxWidth: CENTRAL_CONTAINER_MAX_WIDTH,
            }}
          >
            <Switch>
              <PrivateRoute path="/sites/home">
                <HomePage_HIC />
              </PrivateRoute>
              <PrivateRoute path="/sites/:site_uuid">
                <SitePage />
              </PrivateRoute>
              <PrivateRoute path="/equipments/:equipment_uuid">
                <EquipmentPage />
              </PrivateRoute>
              <PrivateRoute path="/zones/:zone_uuid">
                <ZonePage />
              </PrivateRoute>
              <PrivateRoute path="/impacts/:impact_uuid">
                <ImpactPage />
              </PrivateRoute>
              <PrivateRoute path="/connect">
                <BleConnectPage />
              </PrivateRoute>
              <PrivateRoute path="/settings/general">
                <SettingsGeneral />
              </PrivateRoute>
              <PrivateRoute path="/settings/device">
                <SettingsDevice />
              </PrivateRoute>
              <PrivateRoute path="/">
                <HomePage />
              </PrivateRoute>
            </Switch>
          </div>
        </Content>
        <Footer>
          <FlexCol style={{ gap: 0, alignItems: "center", justifyContent: "center" }}>
            <b>
              <Translated keyEn="Lugsoft Universal" /> H3 © 2018 — {new Date().getFullYear()}
            </b>
            <span>Ver. {process.env.REACT_APP_VER}</span>
          </FlexCol>
        </Footer>
        <Menu theme="dark" mode="horizontal" hidden={!isMobile} items={menuItems} />
      </Layout>
    </Layout>
  )
}

export const AppLayout: FC = () => {
  return (
    <Switch>
      <Route path="/login">
        <LoginPage />
      </Route>
      <PrivateRoute path="/report/:site_uuid">
        <ReportMasterPage />
      </PrivateRoute>
      <Route path="/">
        <MainLayout />
      </Route>
    </Switch>
  )
}
